import { useMemo, useState } from 'react';
import { Section, SettingRow } from '@vnetwork/ui/layout';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useMyServices } from '@/api/services';
import { useWaapCurrentPackage, useChangeWaapPackage } from '@/api/waap';
import { useWaapWebsite } from '../waap-context';
import { waapServiceLabel } from '../waap-service';
import { errMessage } from '../waap-util';
import { WaapDeleteServiceDialog } from '../WaapDeleteServiceDialog';

// Free (4001) is the floor plan — there is no tier below it. Cancelling a paid
// plan downgrades to Free (keeping websites); it never clears the package to
// none, which would strand the vendor entity (CreateWebsite is rejected without a
// package). Leaving WAAP entirely is the separate Delete-service action below.
const FREE_ID = '4001';

/**
 * Danger zone for a WAAP service — mirrors the Compute settings danger zone:
 * "Cancel subscription" (downgrade a paid plan to Free) and "Delete this service".
 * Service-scoped; delete is blocked while websites are still bound.
 */
export function WaapDangerZone({ serviceId }: { serviceId: string }) {
  const { show } = useToast();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const { websites } = useWaapWebsite();
  const { data: services = [] } = useMyServices();
  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const serviceName = service ? waapServiceLabel(service) : '';
  const websiteCount = websites.length;

  // "Cancel subscription" only makes sense on a paid plan — on Free there is
  // nothing to downgrade to, so leaving is the Delete action instead.
  const { data: currentId = '' } = useWaapCurrentPackage(serviceId);
  const isPaid = !!currentId && currentId !== FREE_ID;
  const change = useChangeWaapPackage(serviceId);
  const downgradeToFree = () =>
    change.mutate(FREE_ID, {
      onSuccess: () => {
        show({ tone: 'success', title: 'Downgraded to Free' });
        setCancelOpen(false);
      },
      onError: (e) => show({ tone: 'danger', title: 'Downgrade failed', description: errMessage(e) }),
    });

  return (
    <Section title="Danger zone" description="Destructive actions for this WAAP service.">
      {isPaid && (
        <SettingRow
          title="Cancel subscription"
          description="Downgrades this WAAP service to the Free plan. Your websites stay online; paid-tier protection features stop until you pick a paid plan again."
          control={
            <Button
              variant="danger"
              size="sm"
              disabled={change.isPending}
              onClick={() => setCancelOpen(true)}
            >
              Cancel subscription
            </Button>
          }
        />
      )}

      <SettingRow
        title="Delete this service"
        description={
          websiteCount > 0
            ? `Blocked: ${websiteCount} website${websiteCount === 1 ? '' : 's'} still bound — remove them first.`
            : 'Removes the WAAP service and its protection edge. Recorded in the activity log.'
        }
        control={
          <Button
            variant="danger"
            size="sm"
            disabled={!service || websiteCount > 0}
            onClick={() => setDeleteOpen(true)}
          >
            Delete service
          </Button>
        }
      />

      {service && (
        <WaapDeleteServiceDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          serviceId={serviceId}
          serviceName={serviceName}
          websiteCount={websiteCount}
        />
      )}

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Alert tone="warning" title="Paid features will stop">
              This WAAP service moves to the Free plan. Your websites stay online, but paid-tier
              protection features stop until you select a paid plan again.
            </Alert>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Keep plan
            </Button>
            <Button variant="danger" disabled={change.isPending} onClick={downgradeToFree}>
              {change.isPending ? 'Downgrading…' : 'Cancel subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Section>
  );
}
